import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ErrorStateMatcher } from '@angular/material/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { SettingsService } from '../settings/settings.service';

class ConditionalErrorMatcher implements ErrorStateMatcher {
  constructor(private fn: () => boolean) {}
  isErrorState(_c: FormControl | null, _f: FormGroupDirective | NgForm | null): boolean {
    return this.fn();
  }
}

interface Slot {
  inicio: string; // "YYYY-MM-DD HH:MM:SS"
  fin: string;
}

interface Ejecutivo {
  id: number;
  nombre: string;
  email: string;
  avatar: string;
  cargo: string | null;
}

type Paso = 'cargando' | 'invalido' | 'slots' | 'form' | 'confirmado';

@Component({
  selector: 'app-agenda-ejecutivo',
  standalone: false,
  templateUrl: './agenda-ejecutivo.component.html',
  styleUrl: './agenda-ejecutivo.component.less',
})
export class AgendaEjecutivoComponent implements OnInit {

  readonly logoUrl = 'https://simpliassets12daacw.s3.us-east-2.amazonaws.com/icon.png';

  private token = '';
  paso: Paso = 'cargando';
  ejecutivo: Ejecutivo | null = null;

  selectedDate: Date = new Date();
  slots: Slot[] = [];
  slotsLoading = false;
  slotsError = '';
  selectedSlot: Slot | null = null;

  agendando = false;
  agendarError = '';
  confirmacion: { fecha: string; hora: string; meetLink: string } | null = null;

  empresaNombre = '';
  rut = '';
  rutStatus: 'idle' | 'error' = 'idle';
  contactoNombre = '';
  contactoEmail = '';
  comentario = '';
  formTouched = false;

  empresaNombreErrorMatcher = new ConditionalErrorMatcher(() =>
    this.formTouched && !this.empresaNombre.trim()
  );
  rutErrorMatcher = new ConditionalErrorMatcher(() => this.rutStatus === 'error');
  contactoNombreErrorMatcher = new ConditionalErrorMatcher(() =>
    this.formTouched && !this.contactoNombre.trim()
  );
  contactoEmailErrorMatcher = new ConditionalErrorMatcher(() => !!this.contactoEmailError);

  
  // ── Calendario custom (estilo Calendly) ────────────────────────────────────
  calendarMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  hasSelectedDay = true;
  readonly weekDays = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  private feriados = new Set<string>();
  private feriadosAniosCargados = new Set<number>();

  private async cargarFeriadosSiFalta(anio: number) {
    if (this.feriadosAniosCargados.has(anio)) return;
    this.feriadosAniosCargados.add(anio);
    try {
      const res: any = await this.settings.get_query(1, `cliente/agendamiento/feriados/?anio=${anio}`, {}, true);
      const fechas: string[] = res?._body?.feriados ?? [];
      fechas.forEach(f => this.feriados.add(f));
    } catch {
      // si falla, no bloqueamos nada extra client-side; el backend igual valida al agendar
    }
  }

  get calendarLabel(): string {
    return this.calendarMonth
      .toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })
      .toUpperCase();
  }

  get calendarWeeks(): (Date | null)[][] {
    const year = this.calendarMonth.getFullYear();
    const month = this.calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const offset = (firstDay.getDay() + 6) % 7; // lunes = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: (Date | null)[] = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);

    const weeks: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }

  isDisabledDay(date: Date | null): boolean {
    if (!date) return true;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    if (date < hoy) return true;
    const day = date.getDay();
    if (day === 0 || day === 6) return true;
    return this.feriados.has(this.formatFecha(date));
  }

  isSelectedDay(date: Date | null): boolean {
    return this.hasSelectedDay && !!date && date.toDateString() === this.selectedDate.toDateString();
  }

  isToday(date: Date | null): boolean {
    return !!date && date.toDateString() === new Date().toDateString();
  }

  canGoPrevMonth(): boolean {
    const hoy = new Date();
    return this.calendarMonth.getFullYear() > hoy.getFullYear()
      || (this.calendarMonth.getFullYear() === hoy.getFullYear() && this.calendarMonth.getMonth() > hoy.getMonth());
  }

  private limpiarSeleccionDia() {
    this.hasSelectedDay = false;
    this.selectedSlot = null;
    this.slots = [];
    this.slotsError = '';
  }

  prevMonth() {
    if (!this.canGoPrevMonth()) return;
    this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() - 1, 1);
    this.limpiarSeleccionDia();
    this.cargarFeriadosSiFalta(this.calendarMonth.getFullYear());
  }

  nextMonth() {
    this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + 1, 1);
    this.limpiarSeleccionDia();
    this.cargarFeriadosSiFalta(this.calendarMonth.getFullYear());
  }

  selectDay(date: Date | null) {
    if (this.isDisabledDay(date)) return;
    this.hasSelectedDay = true;
    this.onDateSelected(date);
  }

  get iniciales(): string {
    const nombre = this.ejecutivo?.nombre?.trim();
    if (!nombre) return '';
    const partes = nombre.split(/\s+/);
    const primera = partes[0]?.[0] ?? '';
    const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
    return (primera + ultima).toUpperCase();
  }

  constructor(
    private route: ActivatedRoute,
    public settings: SettingsService,
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.cargarEjecutivo();
  }

  private async cargarEjecutivo() {
    if (!this.token) {
      this.paso = 'invalido';
      return;
    }
    try {
      const res: any = await this.settings.get_query(1, `cliente/agendamiento/ejecutivo/${this.token}/`, {}, true);
      if (res?.status !== 200 || !res?._body) {
        this.paso = 'invalido';
        return;
      }
      this.ejecutivo = res._body;
      this.paso = 'slots';
      this.cargarFeriadosSiFalta(this.calendarMonth.getFullYear());
      this.loadSlots();
    } catch {
      this.paso = 'invalido';
    }
  }

  private formatFecha(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  onDateSelected(date: Date | null) {
    if (!date) return;
    this.selectedDate = date;
    this.selectedSlot = null;
    this.loadSlots();
  }

  async loadSlots() {
    this.slotsLoading = true;
    this.slotsError = '';
    this.slots = [];
    try {
      const fecha = this.formatFecha(this.selectedDate);
      const res: any = await this.settings.get_query(
        1,
        `cliente/agendamiento/disponibilidad/${this.token}/?fecha=${fecha}`,
        {},
        true,
      );
      if (res?.status === 200) {
        this.slots = res._body?.slots ?? [];
      } else {
        this.slotsError = 'No se pudo cargar la disponibilidad. Intenta nuevamente.';
      }
    } catch {
      this.slotsError = 'No se pudo cargar la disponibilidad. Intenta nuevamente.';
    } finally {
      this.slotsLoading = false;
    }
  }

  horaSlot(slot: Slot): string {
    return slot.inicio.slice(11, 16);
  }

  elegirSlot(slot: Slot) {
    this.selectedSlot = slot;
    this.paso = 'form';
  }

  volverASlots() {
    this.paso = 'slots';
    this.agendarError = '';
  }

  // ── RUT (mismo algoritmo que landing-inversiones.component.ts) ────────────
  private isValidRut(rut: string): boolean {
    const clean = rut.replace(/[^0-9kK]/g, '').toUpperCase();
    if (clean.length < 2) return false;
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    if (!/^\d+$/.test(body)) return false;
    let sum = 0;
    let mul = 2;
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body[i], 10) * mul;
      mul = mul === 7 ? 2 : mul + 1;
    }
    const rem = 11 - (sum % 11);
    const expected = rem === 11 ? '0' : rem === 10 ? 'K' : String(rem);
    return dv === expected;
  }

  private formatRut(raw: string): string {
    const clean = raw.replace(/[^0-9kK]/g, '').toUpperCase();
    if (clean.length <= 1) return clean;
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    return `${body}-${dv}`;
  }

  onRutInput(raw: string) {
    this.rut = this.formatRut(raw);
    this.rutStatus = 'idle';
  }

  onRutBlur() {
    if (!this.rut) return;
    this.rutStatus = this.isValidRut(this.rut) ? 'idle' : 'error';
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  get contactoEmailError(): string {
    if (!this.formTouched) return '';
    if (!this.contactoEmail.trim()) return 'El email es obligatorio.';
    return this.isValidEmail(this.contactoEmail) ? '' : 'Ingresa un email válido.';
  }

  get formValido(): boolean {
    return !!this.empresaNombre.trim()
      && !!this.rut && this.isValidRut(this.rut)
      && !!this.contactoNombre.trim()
      && this.isValidEmail(this.contactoEmail);
  }

  async agendar() {
    this.formTouched = true;
    if (this.rut) this.onRutBlur();
    if (!this.formValido || !this.selectedSlot || this.agendando) return;

    this.agendando = true;
    this.agendarError = '';
    try {
      const payload = {
        fecha_inicio: this.selectedSlot.inicio,
        empresa_nombre: this.empresaNombre.trim(),
        rut_empresa: this.rut,
        contacto_nombre: this.contactoNombre.trim(),
        contacto_email: this.contactoEmail.trim(),
        comentario: this.comentario.trim() || null,
      };
      const res: any = await this.settings.get_query(
        2,
        `cliente/agendamiento/agendar/${this.token}/`,
        payload,
        true,
      );
      if (res?.status === 200 || res?.status === 201) {
        this.confirmacion = {
          fecha: res._body.fecha_inicio.slice(0, 10),
          hora: res._body.fecha_inicio.slice(11, 16),
          meetLink: res._body.meet_link,
        };
        this.paso = 'confirmado';
      } else if (res?.status === 409) {
        this.agendarError = res._body?.detail || 'Ese horario ya no está disponible, elige otro.';
        this.paso = 'slots';
        this.loadSlots();
      } else {
        this.agendarError = 'No se pudo agendar la reunión. Intenta nuevamente.';
      }
    } catch (err: any) {
      if (err?.status === 409 || err?.statusCode === 409) {
        this.agendarError = 'Ese horario ya no está disponible, elige otro.';
        this.paso = 'slots';
        this.loadSlots();
      } else {
        this.agendarError = 'No se pudo agendar la reunión. Intenta nuevamente.';
      }
    } finally {
      this.agendando = false;
    }
  }

  agendarOtraReunion() {
    this.selectedSlot = null;
    this.empresaNombre = '';
    this.rut = '';
    this.rutStatus = 'idle';
    this.contactoNombre = '';
    this.contactoEmail = '';
    this.comentario = '';
    this.formTouched = false;
    this.confirmacion = null;
    this.agendarError = '';
    this.paso = 'slots';
    this.loadSlots();
  }
}
